import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const isProduction = process.env.NODE_ENV === 'production';
const mockIsCustomerSupport =
  !isProduction &&
  getEnv('REACT_APP_MOCK_IS_CUSTOMER_SUPPORT', true) === 'true';

/**
 * V2 answer: the `x-hasura-is-customer-support` JWT claim, with the
 * `CustomerSupport` allowed-role as a second reading.
 *
 * Both are login-time snapshots, and the claim is
 * `auth.user."IsCustomerSupport"` — the RiskSmart-EMPLOYEE flag, not the
 * entitlement. An employee with no support access reads `true` here, and a grant
 * or revocation made during the session is invisible until the next login. That
 * is why V3 does not use it (see below); it stays because the V2 role system has
 * no better source and this is the behaviour it has always had.
 */
const useIsCustomerSupportFromJwt = (): boolean => {
  const { user } = useRisksmartUser();

  return (
    user?.isCustomerSupport ||
    user?.allowedRoles?.includes('CustomerSupport') ||
    mockIsCustomerSupport
  );
};

/**
 * Returns whether the current user is a CustomerSupport user.
 *
 * COI-819 — on the V3 role system this is resolved SERVER-SIDE from
 * `auth.user_role` via `role.isCurrentUserCustomerSupport`, the same lookup
 * `assertCustomerSupport` gates writes with. The UI then decides what to render
 * from the same source that decides what the server will accept, and a grant or
 * revocation takes effect without a re-login.
 *
 * The JWT reading is kept for the V2 branch only. It answers a different
 * question (employee, not entitled) from a stale snapshot, so it must not decide
 * anything on V3.
 *
 * Fails closed while the query is in flight: `false` until the server answers,
 * matching `assertCustomerSupport`'s conservative deny on a lookup failure. CS-only
 * affordances therefore appear a beat after load rather than flashing and
 * vanishing for everyone else.
 */
export const useIsCustomerSupport = (): boolean => {
  const trpc = useTRPC();
  const isMultiRoleEnabled = useIsFeatureFlagEnabled('trpc');
  const fromJwt = useIsCustomerSupportFromJwt();

  const { data, isSuccess } = useQuery({
    ...trpc.frontend.role.isCurrentUserCustomerSupport.queryOptions(),
    enabled: isMultiRoleEnabled,
  });

  if (!isMultiRoleEnabled) {
    return fromJwt;
  }

  // An OR against the resolved answer, NOT a default for the unresolved one.
  //
  // `data ?? mockIsCustomerSupport` had both halves backwards: `??` only falls
  // through on `undefined`, so the mock applied while the query was PENDING or
  // had failed — the two states that must fail closed — and was discarded the
  // moment the server said `false`, which is exactly the local-dev case it
  // exists for (a developer with no seeded `CustomerSuccess` row).
  //
  // `mockIsCustomerSupport` is hard-false in production, so this reduces to the
  // server's answer there.
  return (isSuccess && data === true) || mockIsCustomerSupport;
};
